import React, { useState, useEffect } from 'react';
import { X, Share, PlusSquare, Download } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPWABanner: React.FC = () => {
  const [showIOSBanner, setShowIOSBanner] = useState(false);
  const [showAndroidBanner, setShowAndroidBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    // Verificar se já foi dispensado
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    if (dismissed) return;

    // Verificar se já está instalado (standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;
    if (isStandalone) return;

    // Detectar iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);

    if (isIOS && isSafari) {
      // Mostrar banner iOS após 2 segundos
      setTimeout(() => setShowIOSBanner(true), 2000);
    }

    // Capturar evento beforeinstallprompt (Android/Chrome)
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setTimeout(() => setShowAndroidBanner(true), 2000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleDismiss = () => {
    setShowIOSBanner(false);
    setShowAndroidBanner(false);
    localStorage.setItem('pwa-banner-dismissed', 'true');
  };

  const handleInstallAndroid = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setShowAndroidBanner(false);
    }
    setDeferredPrompt(null);
  };

  // Banner para iOS
  if (showIOSBanner) {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-green-500 shadow-lg z-50 animate-slide-up">
        <div className="max-w-lg mx-auto p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Download className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 text-sm">
                Instale o Cartão HMG
              </h3>
              <p className="text-xs text-gray-600 mt-1">
                Adicione à sua tela inicial para acesso rápido
              </p>
              <div className="mt-3 flex items-center gap-2 text-xs text-gray-700 bg-gray-50 rounded-lg p-2">
                <span className="flex items-center gap-1">
                  1. Toque em
                  <Share className="w-4 h-4 text-blue-500" />
                </span>
                <span className="text-gray-400">→</span>
                <span className="flex items-center gap-1">
                  2. <PlusSquare className="w-4 h-4 text-gray-500" />
                  Tela de Início
                </span>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Banner para Android/Chrome
  if (showAndroidBanner && deferredPrompt) {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-green-500 shadow-lg z-50 animate-slide-up">
        <div className="max-w-lg mx-auto p-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Download className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 text-sm">
                Instale o Cartão HMG
              </h3>
              <p className="text-xs text-gray-600 mt-1">
                Acesso rápido direto da sua tela inicial
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDismiss}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Agora não
              </button>
              <button
                onClick={handleInstallAndroid}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                Instalar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default InstallPWABanner;
